/**
 * GoogleAddressMapFeature
 * 
 * Feature que maneja la lógica de un mapa de Google Maps para selección de direcciones.
 * Sincroniza bidireccionalmente entre el mapa y los campos del formulario.
 * 
 * DEPENDENCIAS PARA REUTILIZAR:
 * - npm: mobx
 * 
 * REQUISITOS DEL FORM:
 * El objeto form debe implementar:
 *   - getFieldByName(fieldName): Retorna un field con .value y .updateValue()
 *   - updateMultipleFieldsWithValues(fieldUpdates): Actualiza múltiples campos y notifica cambios
 *   - hasAllValidValues(): Verifica si todos los campos son válidos
 * 
 * REQUISITOS DE LOS FIELDS:
 *   - value: El valor actual del campo
 *   - updateValue(newValue): Actualiza el valor del campo
 * 
 * USO:
 *   const mapFeature = new GoogleAddressMapFeature(
 *       form,
 *       'address',      // nombre del campo de dirección
 *       'jurisdiction', // nombre del campo de provincia
 *       'city',         // nombre del campo de ciudad
 *       'postalCode',   // nombre del campo de código postal
 *       jurisdictionOptions  // opcional: [{label, value}, ...] para mapear el nombre de Google a vuestras jurisdicciones
 *   );
 */
import { action, computed, makeObservable, observable } from "mobx";
import ArgentinePostalCode from "../../domain/ArgentinePostalCode";

/** Alias de nombres que devuelve Google a las jurisdicciones que usamos en el sistema. */
const JURISDICTION_ALIASES = {
    'caba': 'Capital Federal',
    'capital federal': 'Capital Federal',
    'ciudad de buenos aires': 'Capital Federal',
    'ciudad autonoma de buenos aires': 'Capital Federal',
    'provincia de buenos aires': 'Buenos Aires',
    'tierra del fuego, antartida e islas del atlantico sur': 'Tierra del Fuego',
    'tierra del fuego antartida e islas del atlantico sur': 'Tierra del Fuego',
};

export default class GoogleAddressMapFeature {
    constructor(form, addressFieldName = 'address', jurisdictionFieldName = 'jurisdiction', cityFieldName = 'city', postalCodeFieldName = 'postalCode', jurisdictionOptions = null) {
        this._form = form;
        this._addressFieldName = addressFieldName;
        this._jurisdictionFieldName = jurisdictionFieldName;
        this._cityFieldName = cityFieldName;
        this._postalCodeFieldName = postalCodeFieldName;
        this._jurisdictionOptions = jurisdictionOptions;

        this._markerPosition = null;
        this._isGeocoding = false;
        this._geocoder = null;
        this._map = null;
        this._isLoaded = false;
        this._isGoogleMapsLoaded = false;

        this._addressWasAutocompleted = false;
        this._jurisdictionWasAutocompleted = false;
        this._cityWasAutocompleted = false;
        this._postalCodeWasAutocompleted = false;

        makeObservable(this, {
            _markerPosition: observable,
            _isGoogleMapsLoaded: observable,
            _addressWasAutocompleted: observable,
            _jurisdictionWasAutocompleted: observable,
            _cityWasAutocompleted: observable,
            _postalCodeWasAutocompleted: observable,
            markerPosition: computed,
            isGoogleMapsLoaded: computed,
            addressWasAutocompleted: computed,
            jurisdictionWasAutocompleted: computed,
            cityWasAutocompleted: computed,
            postalCodeWasAutocompleted: computed,
            setMarkerPosition: action,
            setIsGoogleMapsLoaded: action,
            geocodeAddress: action,
            reverseGeocode: action,
            updateFromPlaceSelection: action,
            markAutocompletedFields: action,
        });
    }

    get markerPosition() {
        return this._markerPosition;
    }

    get isGoogleMapsLoaded() {
        return this._isGoogleMapsLoaded;
    }

    setMarkerPosition(position) {
        this._markerPosition = position;
        
        if (this._map && position) {
            setTimeout(() => {
                if (this._map && this._markerPosition) {
                    this._map.setCenter(this._markerPosition);
                    this._map.setZoom(15);
                }
            }, 100);
        }
    }

    setIsGoogleMapsLoaded(value) {
        this._isGoogleMapsLoaded = value;
    }

    get addressWasAutocompleted() {
        return this._addressWasAutocompleted;
    }

    get jurisdictionWasAutocompleted() {
        return this._jurisdictionWasAutocompleted;
    }

    get cityWasAutocompleted() {
        return this._cityWasAutocompleted;
    }

    get postalCodeWasAutocompleted() {
        return this._postalCodeWasAutocompleted;
    }

    _hasMeaningfulValue(value) {
        return typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
    }

    markAutocompletedFields({ address, jurisdiction, city, postalCode }) {
        this._addressWasAutocompleted = this._hasMeaningfulValue(address);
        this._jurisdictionWasAutocompleted = this._hasMeaningfulValue(jurisdiction);
        this._cityWasAutocompleted = this._hasMeaningfulValue(city);
        this._postalCodeWasAutocompleted = this._hasMeaningfulValue(postalCode);
    }

    setGeocoder(geocoder) {
        this._geocoder = geocoder;
        this._isLoaded = true;
    }

    setMap(map) {
        this._map = map;
    }

    get addressField() {
        return this._form.getFieldByName(this._addressFieldName);
    }

    get jurisdictionField() {
        return this._form.getFieldByName(this._jurisdictionFieldName);
    }

    get cityField() {
        return this._form.getFieldByName(this._cityFieldName);
    }

    get postalCodeField() {
        return this._form.getFieldByName(this._postalCodeFieldName);
    }

    _normalizeForComparison(inputString) {
        if (!inputString || typeof inputString !== 'string') return '';
        return inputString
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    }

    _mapGoogleJurisdictionToOurs(googleJurisdiction) {
        if (!googleJurisdiction || typeof googleJurisdiction !== 'string') return googleJurisdiction || '';
        const normalized = this._normalizeForComparison(googleJurisdiction);

        if (this._jurisdictionOptions && this._jurisdictionOptions.length > 0) {
            for (const option of this._jurisdictionOptions) {
                const value = typeof option === 'string' ? option : option.value;
                const label = typeof option === 'string' ? option : option.label;
                if (this._normalizeForComparison(value) === normalized || this._normalizeForComparison(label) === normalized) {
                    return value;
                }
            }
            const normalizedWithoutProvinceSuffix = normalized
                .replace(/\s+province$/, '')
                .replace(/\s+provincia$/, '');
            if (normalizedWithoutProvinceSuffix !== normalized) {
                for (const option of this._jurisdictionOptions) {
                    const value = typeof option === 'string' ? option : option.value;
                    const label = typeof option === 'string' ? option : option.label;
                    if (this._normalizeForComparison(value) === normalizedWithoutProvinceSuffix || this._normalizeForComparison(label) === normalizedWithoutProvinceSuffix) {
                        return value;
                    }
                }
            }
            const aliasMatch = JURISDICTION_ALIASES[normalized];
            if (aliasMatch) return aliasMatch;
        } else {
            const aliasMatch = JURISDICTION_ALIASES[normalized];
            if (aliasMatch) return aliasMatch;
        }

        return googleJurisdiction;
    }

    _jurisdictionFrom(rawPostalCode, googleJurisdiction) {
        const postalCode = new ArgentinePostalCode(rawPostalCode);
        if (postalCode.carriesProvinceLetter()) {
            return postalCode.province();
        }
        return this._mapGoogleJurisdictionToOurs(googleJurisdiction);
    }

    _cityForJurisdiction(city, jurisdiction) {
        if (jurisdiction === 'Capital Federal') {
            return 'Capital Federal';
        }
        return city;
    }

    async geocodeAddress(address, jurisdiction, city, retryCount = 0) {
        const maxRetries = 3;
        
        if (!this._geocoder) {
            if (retryCount < maxRetries) {
                setTimeout(() => {
                    this.geocodeAddress(address, jurisdiction, city, retryCount + 1);
                }, 200 * (retryCount + 1));
            }
            return;
        }
        
        if (this._isGeocoding) return;
        
        const fullAddress = [address, city, jurisdiction].filter(Boolean).join(', ');
        if (!fullAddress || fullAddress.trim().length === 0) return;

        this._isGeocoding = true;

        try {
            const result = await new Promise((resolve, reject) => {
                this._geocoder.geocode({ address: fullAddress }, (results, status) => {
                    if (status === 'OK' && results && results.length > 0) {
                        resolve(results[0]);
                    } else {
                        reject(new Error(`Geocoding failed: ${status}`));
                    }
                });
            });

            const location = result.geometry.location;
            const newPosition = {
                lat: location.lat(),
                lng: location.lng(),
            };

            this._markerPosition = newPosition;

            if (this._map) {
                this._map.setCenter(newPosition);
                this._map.setZoom(15);
            }

            const missingJurisdiction = !this.jurisdictionField?.value;
            const missingPostalCode = !this.postalCodeField?.value;
            if (missingJurisdiction || missingPostalCode) {
                this._isGeocoding = false;
                await this.reverseGeocode(newPosition.lat, newPosition.lng);
            }
        } catch (error) {
            console.error('Error geocoding address:', error);
        } finally {
            this._isGeocoding = false;
        }
    }

    async reverseGeocode(lat, lng) {
        if (!this._geocoder || this._isGeocoding) return;

        this._isGeocoding = true;

        try {
            const result = await new Promise((resolve, reject) => {
                this._geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                    if (status === 'OK' && results && results.length > 0) {
                        resolve(results[0]);
                    } else {
                        reject(new Error(`Reverse geocoding failed: ${status}`));
                    }
                });
            });

            const addressComponents = result.address_components;
            
            const streetNumber = this.extractAddressComponent(addressComponents, ['street_number']);
            const route = this.extractAddressComponent(addressComponents, ['route']);
            const postalCode = this.extractAddressComponent(addressComponents, ['postal_code']);
            const jurisdictionRaw = this.extractAddressComponent(addressComponents, ['administrative_area_level_1']);
            const jurisdiction = this._jurisdictionFrom(postalCode, jurisdictionRaw);
            const locality = this._cityForJurisdiction(this.extractLocality(addressComponents), jurisdiction);
            const address = this.buildStreetAddress(route, streetNumber, result.formatted_address);

            await this._form.updateMultipleFieldsWithValues({
                [this._addressFieldName]: address,
                [this._cityFieldName]: locality,
                [this._jurisdictionFieldName]: jurisdiction,
                [this._postalCodeFieldName]: postalCode,
            });

            this.markAutocompletedFields({ address, jurisdiction, city: locality, postalCode });
        } catch (error) {
            console.error('Error reverse geocoding:', error);
        } finally {
            this._isGeocoding = false;
        }
    }

    async updateFromPlaceSelection(placeData) {
        const jurisdiction = this._jurisdictionFrom(placeData.postalCode, placeData.jurisdiction);
        const locality = this._cityForJurisdiction(placeData.locality, jurisdiction);
        await this._form.updateMultipleFieldsWithValues({
            [this._addressFieldName]: placeData.address,
            [this._cityFieldName]: locality,
            [this._jurisdictionFieldName]: jurisdiction,
            [this._postalCodeFieldName]: placeData.postalCode,
        });

        this.markAutocompletedFields({
            address: placeData.address,
            jurisdiction,
            city: locality,
            postalCode: placeData.postalCode,
        });

        if (placeData.location) {
            this.setMarkerPosition(placeData.location);
        }
    }

    extractAddressComponent(addressComponents, types) {
        const component = addressComponents.find(component => 
            types.some(type => component.types.includes(type))
        );
        return component?.long_name || '';
    }

    extractLocality(addressComponents) {
        return (
            this.extractAddressComponent(addressComponents, ['locality']) ||
            this.extractAddressComponent(addressComponents, ['sublocality', 'sublocality_level_1']) ||
            this.extractAddressComponent(addressComponents, ['administrative_area_level_2'])
        );
    }

    buildStreetAddress(route, streetNumber, formattedAddress) {
        if (route && streetNumber) {
            return `${route} ${streetNumber}`.trim();
        }
        if (route) return route;
        if (streetNumber) return streetNumber;
        if (formattedAddress) {
            const firstPart = formattedAddress.split(',')[0]?.trim();
            return firstPart || formattedAddress;
        }
        return '';
    }
}

